/**
 * Stores > Inventory Imports at /stores/hq/imports.
 *
 * Cockpit only (not in The Sweet Spot), admin only. Generates the fleet-wide
 * physical-count file CounterPoint imports: every submitted shelf count not
 * yet in a file, summed per store and item, ignoring replaced rows. Tracks
 * each generated file's status (Generated / Imported / Cancelled) so nobody
 * imports the same file twice, which CounterPoint does not protect against on
 * its own.
 *
 * Top-level catalog tool (reached from the sidebar), so no back link per the
 * Cockpit UI navigation standard.
 *
 * Gated by stores.imports (its own accessSlug, deliberately not folded into
 * Store users / Store tablets, mirroring how Item Sales gets its own
 * accessSlug).
 */

import { useState } from 'react';
import { Check, Download, FileDown, StickyNote, X, XCircle } from 'lucide-react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { RowIconButton } from '@/components/RowActionIcons';
import { Pill } from '@/components/Pill';
import { gridTheadClassName } from '@ampersandbrands/ui';
import { formatDateTime } from '@/lib/dateUtils';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useImportFiles,
  useGenerateImportFile,
  useMarkImportImported,
  useCancelImportFile,
  useSetImportNote,
  importFileDownloadUrl,
  isNotAvailable,
  isOpenImportExists,
  isNothingToImport,
  IMPORT_STATUS_TONES,
  type ImportFile,
} from '@/lib/useStores';
import { AccessDeniedCard, NotAvailableCard, ErrorCard } from './storesShared';
import { InventoryStoreHeader } from './InventoryStoreHeader';

const IMPORTS_TOTAL_COLUMNS = 7;

const STATUS_LABEL: Record<ImportFile['status'], string> = {
  generated: 'Generated',
  imported: 'Imported',
  cancelled: 'Cancelled',
};

export function InventoryImports() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.imports', user?.superAdmin);

  const importsQ = useImportFiles();
  const generateM = useGenerateImportFile();
  const markImportedM = useMarkImportImported();
  const cancelM = useCancelImportFile();
  const noteM = useSetImportNote();

  const [conflictOpen, setConflictOpen] = useState(false);
  const [noteFor, setNoteFor] = useState<ImportFile | null>(null);

  if (user && !canView) return <AccessDeniedCard what="Inventory Imports" />;
  if (isNotAvailable(importsQ.error)) {
    return (
      <div>
        <InventoryStoreHeader title="Inventory Imports" showStorePicker={false} />
        <NotAvailableCard what="Inventory Imports" />
      </div>
    );
  }

  const imports = importsQ.data?.imports ?? [];

  function handleGenerate() {
    generateM.mutate(undefined, {
      onError: (err) => {
        if (isOpenImportExists(err)) setConflictOpen(true);
      },
    });
  }

  function handleCancelAndRegenerate() {
    setConflictOpen(false);
    generateM.mutate({ cancel_previous: true });
  }

  function handleMarkImported(row: ImportFile) {
    if (!window.confirm(`Mark ${row.file_name} as imported? Only do this after it has been uploaded into CounterPoint.`)) return;
    markImportedM.mutate({ id: row.id });
  }

  function handleCancel(row: ImportFile) {
    if (!window.confirm(`Cancel ${row.file_name}? Its counts fall into the next file you generate.`)) return;
    cancelM.mutate({ id: row.id });
  }

  const genError = generateM.error;
  const showGenError = genError && !isOpenImportExists(genError);
  const genErrorMessage = genError && isNothingToImport(genError)
    ? 'Nothing to import. Every submitted count is already in a file.'
    : genError?.message;

  const generateButton = (
    <button
      type="button"
      onClick={handleGenerate}
      disabled={generateM.isPending}
      className="inline-flex items-center gap-1.5 rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-amp-olive-dark"
    >
      <FileDown className="h-4 w-4" aria-hidden />
      {generateM.isPending ? 'Generating...' : 'Generate Import File'}
    </button>
  );

  // Desktop keeps Generate in the toolbar; on a phone the toolbar collapses
  // under the Filters panel, where Generate would DUPLICATE the mobileActions
  // copy that sits next to the Filters button (Steven, 2026-08-10: the one
  // next to Filters is the right one). Hence hidden below md here.
  const toolbar = <div className="hidden items-center gap-3 md:flex">{generateButton}</div>;
  const mobileActions = <div className="flex items-center gap-3 md:hidden">{generateButton}</div>;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <InventoryStoreHeader
        title="Inventory Imports"
        subtitle="The physical-count file CounterPoint imports, built from submitted shelf counts."
        showStorePicker={false}
      />

      {showGenError && (
        <div className="mb-3">
          <ErrorCard message={genErrorMessage ?? 'Could not generate the file.'} />
        </div>
      )}

      {/* mobileActions keeps Generate visible next to the Filters button on a
          phone; the collapsed toolbar hid the page's ONLY action (Steven,
          2026-08-07: "I don't see the generate import button"). */}
      <DataGridShell
        total={imports.length}
        loaded={imports.length}
        countNoun="files"
        toolbar={toolbar}
        mobileActions={mobileActions}
      >
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="px-3 py-2 text-left font-medium">File name</th>
              <th className="px-3 py-2 text-left font-medium">Generated</th>
              <th className="px-3 py-2 text-left font-medium w-[110px]">Status</th>
              <th className="px-3 py-2 text-left font-medium">Imported</th>
              <th className="px-3 py-2 text-right font-medium w-[90px]">Items</th>
              <th className="px-3 py-2 text-left font-medium">Note</th>
              <th className="px-3 py-2 w-[110px] text-right font-medium">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {importsQ.isLoading ? (
              <tr>
                <td colSpan={IMPORTS_TOTAL_COLUMNS} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading import files...
                </td>
              </tr>
            ) : importsQ.error ? (
              <tr>
                <td colSpan={IMPORTS_TOTAL_COLUMNS} className="px-4 py-6">
                  <ErrorCard message={importsQ.error.message} />
                </td>
              </tr>
            ) : imports.length === 0 ? (
              <tr>
                <td colSpan={IMPORTS_TOTAL_COLUMNS} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No import files yet. Generate Import File builds one from every submitted count not yet imported.
                </td>
              </tr>
            ) : (
              imports.map((row) => (
                <ImportTr
                  key={row.id}
                  row={row}
                  markingImported={markImportedM.isPending}
                  cancelling={cancelM.isPending}
                  onMarkImported={() => handleMarkImported(row)}
                  onCancel={() => handleCancel(row)}
                  onOpenNote={() => setNoteFor(row)}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>

      {conflictOpen && (
        <OpenImportConflictDialog
          onCancelAndRegenerate={handleCancelAndRegenerate}
          onGoBack={() => setConflictOpen(false)}
        />
      )}

      {noteFor && (
        <NoteEditModal
          importFile={noteFor}
          saving={noteM.isPending}
          error={noteM.error?.message ?? null}
          onSave={(note) =>
            noteM.mutate({ id: noteFor.id, note }, { onSuccess: () => setNoteFor(null) })
          }
          onClose={() => setNoteFor(null)}
        />
      )}
    </div>
  );
}

function ImportTr({
  row,
  markingImported,
  cancelling,
  onMarkImported,
  onCancel,
  onOpenNote,
}: {
  row: ImportFile;
  markingImported: boolean;
  cancelling: boolean;
  onMarkImported: () => void;
  onCancel: () => void;
  onOpenNote: () => void;
}) {
  const isGenerated = row.status === 'generated';
  return (
    <tr className="bg-amp-white dark:bg-amp-dark-surface">
      <td className="px-3 py-2 text-amp-ink dark:text-amp-dark-ink">
        <span className="font-mono text-xs">{row.file_name}</span>
      </td>
      <td className="px-3 py-2">
        <span className="block text-amp-ink dark:text-amp-dark-ink">{formatDateTime(row.generated_at)}</span>
        <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {row.generated_by_email}
        </span>
      </td>
      <td className="px-3 py-2">
        <Pill tone={IMPORT_STATUS_TONES[row.status]}>{STATUS_LABEL[row.status]}</Pill>
      </td>
      <td className="px-3 py-2">
        {row.status === 'imported' ? (
          <>
            <span className="block text-amp-ink dark:text-amp-dark-ink">{formatDateTime(row.imported_at)}</span>
            <span className="block text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
              {row.imported_by_email}
            </span>
          </>
        ) : row.status === 'cancelled' ? (
          <span className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Cancelled {formatDateTime(row.cancelled_at)}
            {row.cancelled_by_email ? ` by ${row.cancelled_by_email}` : ''}
          </span>
        ) : (
          <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">--</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-amp-ink dark:text-amp-dark-ink">
        {row.line_count}
        <span className="ml-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
          / {row.session_count} {row.session_count === 1 ? 'count' : 'counts'}
        </span>
      </td>
      <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">
        <span className="block max-w-[220px] truncate">{row.note ?? '--'}</span>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center justify-end gap-3">
          <a
            href={importFileDownloadUrl(row.id)}
            title="Open file"
            aria-label="Open file"
            className="inline-flex items-center text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <Download className="h-4 w-4" aria-hidden />
          </a>
          {isGenerated && (
            <RowIconButton
              icon={Check}
              label="Mark imported"
              disabled={markingImported}
              onClick={onMarkImported}
            />
          )}
          {isGenerated && (
            <RowIconButton
              icon={XCircle}
              label="Cancel"
              disabled={cancelling}
              onClick={onCancel}
            />
          )}
          <RowIconButton icon={StickyNote} label="Note" onClick={onOpenNote} />
        </div>
      </td>
    </tr>
  );
}

function OpenImportConflictDialog({
  onCancelAndRegenerate,
  onGoBack,
}: {
  onCancelAndRegenerate: () => void;
  onGoBack: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onGoBack}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="A file is already generated"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface"
      >
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-2">
          A file is already generated
        </h2>
        <p className="text-sm text-amp-ink dark:text-amp-dark-ink mb-4">
          Someone generated a file that was never marked imported. Cancel it and roll its counts
          into a new file, or go back and mark the existing file imported first.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onGoBack}
            className="rounded-md border border-amp-line px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            Go back
          </button>
          <button
            type="button"
            onClick={onCancelAndRegenerate}
            className="rounded-md bg-amp-olive px-4 py-2 text-sm font-medium text-white dark:bg-amp-olive-dark"
          >
            Cancel previously generated file
          </button>
        </div>
      </div>
    </div>
  );
}

function NoteEditModal({
  importFile,
  saving,
  error,
  onSave,
  onClose,
}: {
  importFile: ImportFile;
  saving: boolean;
  error: string | null;
  onSave: (note: string) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(importFile.note ?? '');
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Note for ${importFile.file_name}`}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-lg border border-amp-line bg-amp-white p-5 shadow-2 dark:border-amp-dark-line dark:bg-amp-dark-surface"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          aria-label="Close"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Note</p>
        <h2 className="text-lg font-semibold text-amp-ink dark:text-amp-dark-ink mb-3">
          <span className="font-mono text-sm">{importFile.file_name}</span>
        </h2>
        {/* Stays a plain box. This note shows in a single-line truncated grid
            cell, which would cut markup mid-tag, and the column is only
            VARCHAR(500) while markup roughly doubles a note. */}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          placeholder="Add a note..."
          aria-label="Note"
          className="w-full rounded border border-amp-line bg-amp-white px-3 py-2 text-sm text-amp-ink dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
        />
        {error && <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p>}
        <div className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-amp-line px-3 py-1.5 text-sm text-amp-ink dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSave(draft.trim())}
            disabled={saving}
            className="rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-amp-olive-dark"
          >
            {saving ? 'Saving...' : 'Save note'}
          </button>
        </div>
      </div>
    </div>
  );
}
